// ============================================================
// Phexotial — Centralized Configuration
// ============================================================
// Single source of truth for environment variables, localStorage
// keys, and typed boot-preference persistence.
// ============================================================

// --- Environment Variables ---

const ENV = import.meta.env;

export const FEED_GATEWAY_URL: string = ENV.VITE_FEED_GATEWAY_URL?.trim() ?? '';

// --- Extension Integration ---

/** Dev / unpacked extension ID (32-char a-p string from chrome://extensions). */
export const DEV_EXTENSION_ID: string = ENV.VITE_PHEXOTIAL_DEV_EXTENSION_ID?.trim() ?? '';

/** Edge Add-ons extension ID (32-char a-p string). Empty in dev/unknown. */
export const EDGE_EXTENSION_ID: string = ENV.VITE_PHEXOTIAL_EDGE_EXTENSION_ID?.trim() ?? '';

/** Chrome Web Store extension ID (32-char a-p string). Empty in dev/unknown. */
export const CHROME_EXTENSION_ID: string = ENV.VITE_PHEXOTIAL_CHROME_EXTENSION_ID?.trim() ?? '';

/** Edge Add-ons listing URL. */
export const EDGE_STORE_URL: string = ENV.VITE_PHEXOTIAL_EDGE_STORE_URL?.trim() ?? '';

/** Chrome Web Store listing URL. */
export const CHROME_STORE_URL: string = ENV.VITE_PHEXOTIAL_CHROME_STORE_URL?.trim() ?? '';

/** Canonical website origin for external messaging. */
export const PHEXOTIAL_ORIGIN = 'https://phexotial.com';

/**
 * Returns the extension ID for the current browser family.
 * Falls back to the dev/unpacked ID for any Chromium-based browser.
 * Returns empty string if no ID is configured.
 */
export function getExtensionIdForBrowser(family: 'edge' | 'chrome' | 'chromium' | 'firefox' | 'safari' | 'unknown'): string {
  if (family === 'edge') return EDGE_EXTENSION_ID || DEV_EXTENSION_ID;
  if (family === 'chrome') return CHROME_EXTENSION_ID || DEV_EXTENSION_ID;
  if (family === 'chromium') return DEV_EXTENSION_ID;
  return '';
}

/**
 * Returns the store URL for the current browser family.
 * Returns empty string if no URL is configured.
 */
export function getStoreUrlForBrowser(family: 'edge' | 'chrome' | 'chromium' | 'firefox' | 'safari' | 'unknown'): string {
  if (family === 'edge') return EDGE_STORE_URL;
  if (family === 'chrome') return CHROME_STORE_URL;
  return '';
}

/** True if any extension ID is configured (not all browsers may have one). */
export function hasConfiguredExtensionId(): boolean {
  return EDGE_EXTENSION_ID.length > 0 || CHROME_EXTENSION_ID.length > 0 || DEV_EXTENSION_ID.length > 0;
}

// --- User-Configured Extension ID ---

const CUSTOM_EXTENSION_ID_KEY = 'io-custom-extension-id';

/** Returns the user-configured extension ID from localStorage, if any. */
export function getCustomExtensionId(): string {
  try {
    return (localStorage.getItem(CUSTOM_EXTENSION_ID_KEY) ?? '').trim();
  } catch {
    return '';
  }
}

/** Persist a user-configured extension ID to localStorage. */
export function setCustomExtensionId(id: string): void {
  try {
    const trimmed = id.trim();
    if (trimmed) {
      localStorage.setItem(CUSTOM_EXTENSION_ID_KEY, trimmed);
    } else {
      localStorage.removeItem(CUSTOM_EXTENSION_ID_KEY);
    }
  } catch {
    // LocalStorage may be unavailable in hardened/private browser contexts.
  }
}

// --- localStorage Keys ---

export const STORAGE_KEYS = {
  /** Persisted user preferences (theme, motion, personalization, etc.) */
  bootPrefs: 'io-boot-prefs',
  /** User-configured feed gateway endpoint override */
  corsProxy: 'io-cors-proxy',
  /** Tracks whether the user has made a layout choice (dismisses startup screen) */
  layoutChoice: 'io-layout-choice',
} as const;

// --- Boot Preferences ---

/**
 * Typed read/write helpers for the `io-boot-prefs` localStorage key.
 * Replaces 6+ duplicated read-parse-mutate-serialize-write blocks in store.ts.
 */
export type BootPrefs = Record<string, unknown>;

export function loadBootPrefs(): BootPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.bootPrefs);
    if (!raw) return {};
    return JSON.parse(raw) as BootPrefs;
  } catch {
    return {};
  }
}

export function saveBootPrefs(prefs: BootPrefs): void {
  try {
    localStorage.setItem(STORAGE_KEYS.bootPrefs, JSON.stringify(prefs));
  } catch {
    // LocalStorage may be unavailable in hardened/private browser contexts.
  }
}

/**
 * Update a single field in boot prefs without losing other fields.
 * Reads the current prefs, merges the update, and writes back atomically.
 */
export function updateBootPrefs(updates: BootPrefs): void {
  const current = loadBootPrefs();
  saveBootPrefs({ ...current, ...updates });
}

// --- URL Validation ---

/**
 * Returns true if the given string is a valid http: or https: URL.
 * Used across bookmarks, projects, feeds, and imports for consistent URL validation.
 */
export function isValidHttpUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}
